import React, { useEffect, useRef, useState } from "react";
import GradientOverlay from "../ui/GradientOverlay";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const AboutHero = ({ heroImage, title, subtitle, breadcrumbs = [] }) => {
  const imgRef = useRef(null);
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Handle image load
  const handleImageLoad = () => {
    setLoaded(true);
  };

  useEffect(() => {
    if (!imgRef.current || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(imgRef.current,
        { scale: 1.1, y: 0 },
        { scale: 1, duration: 1.5, ease: "power2.out" }
      );

      gsap.to(imgRef.current, {
        y: 150,
        scale: 1.15,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      gsap.fromTo(contentRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: 0.3,
          ease: "power2.out"
        }
      );
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, [loaded]);

  return (
    <section ref={sectionRef} className="w-full relative overflow-hidden -mt-[20px] z-10">
      <GradientOverlay className="min-h-[420px] md:min-h-[520px] flex items-end">
        
        {/* Background image with enhanced GSAP parallax */}
        <div className="w-full h-full absolute top-0 left-0 overflow-hidden">
          <img
            ref={imgRef}
            src={heroImage}
            alt={`${title} page hero banner`}
            onLoad={handleImageLoad}
            className="w-full h-full object-cover will-change-transform scale-[1.05]"
            style={{ 
              transformOrigin: 'center center',
              marginTop: '-20px',
              height: 'calc(100% + 20px)'  /* Extend image to cover the gap */
            }}
          />
        </div>

        {/* Animated content */}
        <div className="container-custom mx-auto px-4 py-4 md:py-0 mb-0 md:mb-[50px] relative z-10">
          <div ref={contentRef} className="max-w-3xl opacity-0">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-[LTWave]">
              {title}
            </h1>

            {/* Breadcrumb Navigation */}
            <div className="mb-6 md:mb-8">
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2">
                  <li>
                    <Link
                      to="/"
                      className="text-white hover:text-white/80 text-sm md:text-base font-inter font-medium"
                    >
                      Home
                    </Link>
                  </li>
                  <li className="flex items-center">
                    <span className="text-white/60 mx-2">&gt;</span>
                    <span className="text-white/90 text-sm md:text-base font-medium font-inter">
                      {title}
                    </span>
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </GradientOverlay>
    </section>
  );
};

export default AboutHero;
